import { logNetRequest } from '@/utils';
import { handle400Statuses, sendApiRequest } from './api';

const TEMPLATES_URL = 'v1/genai/templates';

const CREATE_TEMPLATE_TYPE_FORM = 'form';

const normalizeCreatedTemplateId = (data) => {
  if (data == null || typeof data !== 'object') {
    return null;
  }
  const id = data.id ?? data.template?.id ?? data.template_id;
  return id != null ? id : null;
};
export const DEFAULT_DOCUMENT_TEMPLATE_FORM_FIELD = {
  agent_name: null,
  answer_format: '',
  clarification: '',
  heading: null,
  id: null,
  num_query_runs: null,
  query_max_answer_tokens: null,
  query_max_context_tokens: null,
  question: '',
  search_terms: '',
  sequence_number: null,
  sub_key_items: null,
  sub_key_mode: null,
  template_key: '',
};

const normalizeTemplateList = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  return data?.documents || data?.templates || [];
};

const normalizeFormFields = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  return data?.form_fields || [];
};

const normalizeSlideFields = (data) => {
  if (Array.isArray(data)) {
    return data.map((slideField) => normalizeSlideField(slideField));
  }

  return (data?.slide_fields || []).map((slideField) => normalizeSlideField(slideField));
};

const normalizeFormField = (data) => {
  const formField = data?.form_field || data?.field || data;
  const normalizedSubKeyItems = normalizeSubKeyItems(formField?.sub_key_items);
  const { options, ...rest } = formField || {};

  return {
    ...DEFAULT_DOCUMENT_TEMPLATE_FORM_FIELD,
    ...rest,
    sub_key_items: normalizedSubKeyItems.value,
    _subKeyItemsError: normalizedSubKeyItems.error,
  };
};

const normalizeSlideField = (data) => {
  const slideField = data?.slide_field || data?.field || data;
  const normalizedSubKeyItems = normalizeSubKeyItems(slideField?.sub_key_items);
  const { options, ...rest } = slideField || {};

  return {
    ...DEFAULT_DOCUMENT_TEMPLATE_FORM_FIELD,
    ...rest,
    sub_key_items: normalizedSubKeyItems.value,
    _subKeyItemsError: normalizedSubKeyItems.error,
  };
};

const getErrorMessage = (error, fallback = 'Something went wrong') => {
  if (typeof error === 'string' && error) {
    return error;
  }

  return fallback;
};

const normalizeSubKeyItems = (value) => {
  if (value === null || value === undefined || value === '') {
    return { value: null, error: null };
  }

  if (typeof value === 'string') {
    try {
      const parsedValue = JSON.parse(value);
      return normalizeSubKeyItems(parsedValue);
    } catch (error) {
      return { value: null, error: 'Unable to load sub key items because the JSON is invalid.' };
    }
  }

  if (typeof value === 'object') {
    return { value, error: null };
  }

  return {
    value: null,
    error: 'Unable to load sub key items because the value is not editable JSON.',
  };
};

const serializeFormField = (formField) => {
  const { options, _subKeyItemsError, ...rest } = formField || {};
  const normalizedSubKeyItems = normalizeSubKeyItems(rest.sub_key_items);

  if (normalizedSubKeyItems.error) {
    return { ok: false, error: normalizedSubKeyItems.error };
  }

  const serializedData = {
    ...rest,
    sub_key_items: normalizedSubKeyItems.value === null ? null : JSON.stringify(normalizedSubKeyItems.value),
  };

  return {
    ok: true,
    data: Object.fromEntries(Object.entries(serializedData).filter(([, value]) => value !== null)),
  };
};

export const fetchAvailableRfpTemplates = async () => {
  const { status, data, error } = await sendApiRequest('get', TEMPLATES_URL);
  logNetRequest(TEMPLATES_URL, status, data, error);
  handle400Statuses(status);

  if (status === 200) {
    return { ok: true, data: normalizeTemplateList(data) };
  }

  return { ok: false, status, error: getErrorMessage(error) };
};

export const fetchAvailableRfpDocs = fetchAvailableRfpTemplates;

/** Templates with explicit `runnable: false` are hidden from the Generate Report UI only; editing UIs use the unfiltered list. */
export const isRunnableTemplate = (template) => template?.runnable !== false;

export const createRfpTemplate = async ({ name, additional_instructions: additionalInstructions }) => {
  const body = {
    name,
    additional_instructions: additionalInstructions ?? '',
    template_type: CREATE_TEMPLATE_TYPE_FORM,
  };
  const { status, data, error } = await sendApiRequest('post', TEMPLATES_URL, body);
  logNetRequest(TEMPLATES_URL, status, data, error);
  handle400Statuses(status);

  if (status === 200 || status === 201) {
    const id = normalizeCreatedTemplateId(data);
    if (id == null) {
      return { ok: false, status, error: 'Create succeeded but no template id was returned.' };
    }
    const resolvedName = data?.name ?? data?.template?.name ?? name;
    return { ok: true, data: { id, name: resolvedName } };
  }

  return { ok: false, status, error: getErrorMessage(error, 'Something went wrong creating the template.') };
};

export const fetchRfpTemplateFormFields = async (templateId) => {
  const URL = `v1/genai/templates/${templateId}/form_fields`;
  const { status, data, error } = await sendApiRequest('get', URL);
  logNetRequest(URL, status, data, error);
  handle400Statuses(status);

  if (status === 200) {
    return { ok: true, data: normalizeFormFields(data) };
  }

  return { ok: false, status, error: getErrorMessage(error, 'Something went wrong loading template fields.') };
};

export const createRfpTemplateFormField = async (templateId, formField) => {
  const URL = `v1/genai/templates/${templateId}/form_fields`;
  const serializedFormField = serializeFormField(formField);

  if (!serializedFormField.ok) {
    return { ok: false, status: 400, error: serializedFormField.error };
  }

  const { status, data, error } = await sendApiRequest('post', URL, serializedFormField.data);
  logNetRequest(URL, status, data, error);
  handle400Statuses(status);

  if (status === 200) {
    return { ok: true, data: normalizeFormField(data) };
  }

  return { ok: false, status, error: getErrorMessage(error, 'Something went wrong creating the field.') };
};

export const updateRfpTemplateFormField = async (templateId, fieldId, formField) => {
  const URL = `v1/genai/templates/${templateId}/form_fields/${fieldId}`;
  const serializedFormField = serializeFormField(formField);

  if (!serializedFormField.ok) {
    return { ok: false, status: 400, error: serializedFormField.error };
  }

  const { status, data, error } = await sendApiRequest('patch', URL, serializedFormField.data);
  logNetRequest(URL, status, data, error);
  handle400Statuses(status);

  if (status === 200) {
    return { ok: true, data: normalizeFormField(data) };
  }

  return { ok: false, status, error: getErrorMessage(error, 'Something went wrong saving the field.') };
};

export const deleteRfpTemplateFormField = async (templateId, fieldId) => {
  const URL = `v1/genai/templates/${templateId}/form_fields/${fieldId}`;
  const { status, data, error } = await sendApiRequest('delete', URL);
  logNetRequest(URL, status, data, error);
  handle400Statuses(status);

  if (status === 200) {
    return { ok: true, data };
  }

  return { ok: false, status, error: getErrorMessage(error, 'Something went wrong deleting the field.') };
};

export const fetchRfpTemplateSlideFields = async (templateId) => {
  const URL = `v1/genai/templates/${templateId}/slide_fields`;
  const { status, data, error } = await sendApiRequest('get', URL);
  logNetRequest(URL, status, data, error);
  handle400Statuses(status);

  if (status === 200) {
    return { ok: true, data: normalizeSlideFields(data) };
  }

  return { ok: false, status, error: getErrorMessage(error, 'Something went wrong loading slide fields.') };
};

export const createRfpTemplateSlideField = async (templateId, slideField) => {
  const URL = `v1/genai/templates/${templateId}/slide_fields`;
  const serializedSlideField = serializeFormField(slideField);

  if (!serializedSlideField.ok) {
    return { ok: false, status: 400, error: serializedSlideField.error };
  }

  const { status, data, error } = await sendApiRequest('post', URL, serializedSlideField.data);
  logNetRequest(URL, status, data, error);
  handle400Statuses(status);

  if (status === 200 || status === 201) {
    return { ok: true, data: normalizeSlideField(data) };
  }

  return { ok: false, status, error: getErrorMessage(error, 'Something went wrong creating the slide field.') };
};

export const updateRfpTemplateSlideField = async (templateId, fieldId, slideField) => {
  const URL = `v1/genai/templates/${templateId}/slide_fields/${fieldId}`;
  const serializedSlideField = serializeFormField(slideField);

  if (!serializedSlideField.ok) {
    return { ok: false, status: 400, error: serializedSlideField.error };
  }

  const { status, data, error } = await sendApiRequest('patch', URL, serializedSlideField.data);
  logNetRequest(URL, status, data, error);
  handle400Statuses(status);

  if (status === 200) {
    return { ok: true, data: normalizeSlideField(data) };
  }

  return { ok: false, status, error: getErrorMessage(error, 'Something went wrong saving the slide field.') };
};

export const deleteRfpTemplateSlideField = async (templateId, fieldId) => {
  const URL = `v1/genai/templates/${templateId}/slide_fields/${fieldId}`;
  const { status, data, error } = await sendApiRequest('delete', URL);
  logNetRequest(URL, status, data, error);
  handle400Statuses(status);

  if (status === 200) {
    return { ok: true, data };
  }

  return { ok: false, status, error: getErrorMessage(error, 'Something went wrong deleting the slide field.') };
};
